import {useCallback, useEffect} from 'react';
import {useMarketersStore} from '../store/marketersStore';
import {useMarketersUIStore} from '../store/marketersUIStore';
import {useNotification} from '../../../Services/useNotification';
import {CreateMarketerRequest} from '../models/CreateMarketer';
import {UpdateMarketerProfileRequest, UpdateMarketerPasswordRequest} from '../models/UpdateMarketer';

/**
 * Unified hook for marketers management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useMarketersManagement = () => {
  // Data stores
  const marketersStore = useMarketersStore();
  const uiStore = useMarketersUIStore();
  const {success: showSuccess} = useNotification();

  // Destructure from marketersStore
  const {
    marketers, pagination, loading, error,
    fetchMarketers, createMarketer, updateMarketerProfile, updateMarketerPassword, deleteMarketer,
    setPagination, refreshData, clearErrors
  } = marketersStore;

  // Destructure from uiStore
  const {
    activeTab, setActiveTab,
    hasNewMarketer, setHasNewMarketer,
    search, setSearch,
    deletingMarketerId, setDeletingMarketerId,
    isAddModalOpen, openAddModal, closeAddModal,
    addFormData, addFormErrors, addFormShowPassword,
    setAddFormData, setAddFormErrors, setAddFormShowPassword, resetAddForm,
    editFormData, editFormErrors, isEditModalOpen, editingMarketer,
    setEditFormData, setEditFormErrors, openEditModal, closeEditModal, resetEditForm,
    changePasswordData, changePasswordErrors, changePasswordShowPassword, isChangePasswordModalOpen, changingPasswordMarketer,
    setChangePasswordData, setChangePasswordErrors, setChangePasswordShowPassword, openChangePasswordModal, closeChangePasswordModal, resetChangePasswordForm,
    reset: resetUI
  } = uiStore;

  // Initial data fetch on mount
  useEffect(() => {
    fetchMarketers();
  }, [fetchMarketers]);

  // Unified Actions
  const handleTabChange = useCallback((tab: 'list' | 'add') => {
    setActiveTab(tab);
    if (tab === 'list') {
      setHasNewMarketer(false);
    }
  }, [setActiveTab, setHasNewMarketer]);

  const handleAddMarketerSuccess = useCallback(() => {
    closeAddModal();
  }, [closeAddModal]);

  const handleDeleteMarketer = useCallback(async (id: number) => {
    setDeletingMarketerId(id);
    try {
      const success = await deleteMarketer(id);
      return success;
    } catch (error) {
      throw error;
    } finally {
      setDeletingMarketerId(null);
    }
  }, [deleteMarketer, setDeletingMarketerId]);

  const handleEditMarketer = useCallback((marketer: any) => {
    openEditModal(marketer);
  }, [openEditModal]);

  const handleEditMarketerSuccess = useCallback(async (marketerData: UpdateMarketerProfileRequest) => {
    if (!editingMarketer) {return;}

    try {
      await updateMarketerProfile(editingMarketer.id, marketerData);
      showSuccess('تم تحديث بيانات المسوق بنجاح');
      closeEditModal();
    } catch (error) {
    }
  }, [updateMarketerProfile, editingMarketer, closeEditModal]);

  const handleChangePassword = useCallback((marketer: any) => {
    openChangePasswordModal(marketer);
  }, [openChangePasswordModal]);

  const handleChangePasswordSuccess = useCallback(async (passwordData: UpdateMarketerPasswordRequest) => {
    if (!changingPasswordMarketer) {return;}

    try {
      await updateMarketerPassword(changingPasswordMarketer.id, passwordData);
      showSuccess('تم تغيير كلمة المرور بنجاح');
      closeChangePasswordModal();
    } catch (error) {
    }
  }, [updateMarketerPassword, changingPasswordMarketer, closeChangePasswordModal]);

  const handlePageChange = useCallback((newPage: number) => {
    const currentLimit = pagination?.limit || 20;
    fetchMarketers(newPage, currentLimit);
  }, [fetchMarketers, pagination?.limit]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    fetchMarketers(1, newPageSize);
  }, [fetchMarketers]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    clearErrors();
    resetUI();
  }, [clearErrors, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    hasNewMarketer, setHasNewMarketer,
    search, setSearch,
    deletingMarketerId, setDeletingMarketerId,

    // Add Modal State
    isAddModalOpen, openAddModal, closeAddModal,

    // Add Form State
    addFormData, addFormErrors, addFormShowPassword,
    setAddFormData, setAddFormErrors, setAddFormShowPassword, resetAddForm,

    // Edit Form State
    editFormData, editFormErrors, isEditModalOpen, editingMarketer,
    setEditFormData, setEditFormErrors, openEditModal, closeEditModal, resetEditForm,

    // Change Password Form State
    changePasswordData, changePasswordErrors, changePasswordShowPassword, isChangePasswordModalOpen, changingPasswordMarketer,
    setChangePasswordData, setChangePasswordErrors, setChangePasswordShowPassword, openChangePasswordModal, closeChangePasswordModal, resetChangePasswordForm,

    // Data
    marketers, pagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleTabChange, handleAddMarketerSuccess, handleDeleteMarketer,
    handleEditMarketer, handleEditMarketerSuccess,
    handleChangePassword, handleChangePasswordSuccess,
    handlePageChange, handlePageSizeChange,
    fetchMarketers, createMarketer, updateMarketerProfile, updateMarketerPassword, deleteMarketer,
    refreshData, resetAllStores
  };
};
